import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Search, Clock, Smartphone, TrendingUp, Camera } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

export const BentoFeatures = () => {
  const { t } = useLanguage();
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' });

  const features = [
    {
      icon: Search,
      title: t.features.seoTitle,
      description: t.features.seoDesc,
      span: 'col-span-1 md:col-span-2 row-span-1',
      highlight: true,
    },
    {
      icon: Clock,
      title: t.features.turnaroundTitle,
      description: t.features.turnaroundDesc,
      span: 'col-span-1 row-span-1',
    },
    {
      icon: Smartphone,
      title: t.features.platformTitle,
      description: t.features.platformDesc,
      span: 'col-span-1 row-span-1',
    },
    {
      icon: Camera,
      title: t.features.photoTitle,
      description: t.features.photoDesc,
      span: 'col-span-1 row-span-1',
    },
    {
      icon: TrendingUp,
      title: t.features.rankingTitle,
      description: t.features.rankingDesc,
      span: 'col-span-1 md:col-span-2 row-span-1',
      stats: t.features.rankingStat,
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: 'easeOut' },
    },
  };

  return (
    <section 
      id="features" 
      ref={sectionRef}
      className="py-20 md:py-32 relative"
      data-testid="features-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-[#FFB703] text-sm font-medium uppercase tracking-wider mb-4 block">
            {t.features.label}
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-semibold mb-4">
            {t.features.title}
          </h2>
          <p className="text-white/60 text-lg max-w-2xl mx-auto">
            {t.features.subtitle}
          </p>
        </motion.div>

        {/* Bento Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6"
          data-testid="bento-grid"
        >
          {features.map((feature, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              className={`bento-card bg-[#121212] border border-white/10 rounded-2xl p-6 md:p-8 hover:border-white/20 transition-colors duration-300 ${feature.span} ${
                feature.highlight ? 'relative overflow-hidden' : ''
              }`}
              data-testid={`feature-card-${index}`}
            >
              {/* Background gradient for highlighted cards */}
              {feature.highlight && (
                <div className="absolute -top-20 -right-20 w-40 h-40 bg-[#F59E0B]/10 rounded-full blur-[60px]" />
              )}

              <div className="relative z-10">
                {/* Icon */}
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                  feature.highlight 
                    ? 'bg-gradient-to-br from-[#F59E0B] to-[#FFB703]' 
                    : 'bg-white/5 border border-white/10'
                }`}>
                  <feature.icon className={`w-6 h-6 ${feature.highlight ? 'text-[#0d0a07]' : 'text-[#FFB703]'}`} />
                </div>

                {/* Content */}
                <h3 className="text-xl md:text-2xl font-semibold text-white mb-2">
                  {feature.title}
                </h3>
                <p className="text-white/60 text-sm md:text-base leading-relaxed">
                  {feature.description}
                </p>

                {/* Stats Badge */}
                {feature.stats && (
                  <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#10B981]/10 border border-[#10B981]/20">
                    <TrendingUp className="w-4 h-4 text-[#10B981]" />
                    <span className="text-sm font-semibold text-[#10B981]">{feature.stats}</span>
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
